// @ts-check
// apolo/src/lib/apiContracts.js — P4-T04 (software-to-9).
//
// Typed FE↔Worker contract for the hottest seam (POST /agent-turn). hono/client (hc)
// gives compile-time RPC types but requires a TS build; in this JS codebase the
// achievable equivalent is a JSDoc @typedef contract type-checked by a SCOPED tsconfig
// (apolo/tsconfig.contracts.json includes ONLY this file, so the JSX noise in the rest
// of apolo/src never enters the check). The shapes MIRROR the Worker's runtime zod
// schema (apolo-api/src/lib/agentTurnSchema.js, P3-T01) — the two halves of the
// contract: zod validates at the Worker edge, this types the FE call site. Keep them
// in lockstep; a FE call with the wrong shape is a tsc error here.
//
// P2-T01/T02 (code-tab-lift) — the IDs that cross this seam are BRANDED
// (brandedIds.js): the session id is a SessionId and the per-turn dedupe key is a
// RunId, so a UserId or a raw string can't silently flow into the wrong slot at a
// checked call site (that's a tsc error). At runtime they ARE strings (the as*
// helpers are identity) — construct them with asSessionId/asRunId at the boundary.

import { asSessionId, asRunId } from './brandedIds.js';

/** @typedef {import('./brandedIds.js').SessionId} SessionId */
/** @typedef {import('./brandedIds.js').RunId} RunId */

/**
 * The /agent-turn request body (mirror of AgentTurnBodySchema).
 * @typedef {Object} AgentTurnRequest
 * @property {Array<{ role: string, content: unknown }>} messages  non-empty (schema enforces min(1))
 * @property {string} [model]
 * @property {Array<unknown>} [tools]
 * @property {boolean} [spec_probe]
 * @property {string} [reasoningEffort]  speed selector: none…max, forwarded to the model
 * @property {RunId} [idempotency_key]  branded: the per-run dedupe key, never a session/user id
 */

/**
 * apiFetch's envelope for the agent-turn response.
 * @typedef {Object} AgentTurnEnvelope
 * @property {boolean} ok
 * @property {number} status
 * @property {unknown} [data]
 * @property {string} [error]
 */

/**
 * Build the /agent-turn request for apiFetch, with the body shape type-checked.
 * The seam ID is a branded SessionId — passing a UserId/RunId/raw string at a
 * checked call site is a tsc error (P2-T02 teeth).
 * @param {SessionId} sessionId
 * @param {AgentTurnRequest} req
 * @returns {{ path: string, method: 'POST', body: AgentTurnRequest }}
 */
export function agentTurnRequest(sessionId, req) {
  return { path: `/api/code-ide/sessions/${sessionId}/agent-turn`, method: 'POST', body: req };
}

/**
 * A canonical, correctly-shaped example. The tsc check on this file verifies it
 * satisfies AgentTurnRequest — change `messages` to a non-array (or drop it) and the
 * scoped `npm run tsc:fe-contract` fails. That is the seam's teeth.
 * @type {AgentTurnRequest}
 */
export const AGENT_TURN_EXAMPLE = {
  messages: [{ role: 'user', content: 'oi' }],
  model: 'kimi/kimi-k2.5',
};

/**
 * P2-T02 — branded-seam self-check. Constructs the IDs at the boundary (asSessionId /
 * asRunId) and threads them through the seam. The tsc check verifies the brands line
 * up; swap asSessionId→asUserId below and `npm run tsc:fe-contract` fails (UserId is
 * not assignable to SessionId) — that is the wrong-id-can't-cross-the-seam teeth.
 */
export const AGENT_TURN_SEAM_SELFCHECK = agentTurnRequest(asSessionId('s1'), {
  messages: [{ role: 'user', content: 'oi' }],
  idempotency_key: asRunId('run-1'),
});
